/**
 * WelcomeWizard — container do Welcome Wizard.
 *
 * - Deslize VERTICAL: cada tela ocupa 100% da área útil; o trilho desliza em Y.
 * - Cada step só é MONTADO quando alcançado (telas futuras = slide vazio) —
 *   assim nenhum step roda efeito de montagem (ex.: o POST do Step9) antes da hora.
 * - Barra de progresso única e fixa no topo (some na Tela 0).
 * - Sem navegação por teclado (setas) — só os botões de cada tela navegam.
 * - Esconde o widget de suporte enquanto o wizard está aberto (evitar fuga do usuário).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Link } from '@mui/material';
import { useWelcomeWizard } from '../../hooks/useWelcomeWizard';
import { useInactivityTimer } from '../../hooks/useInactivityTimer';
import WelcomeProgressBar from './WelcomeProgressBar';
import WelcomeStepBoundary from './WelcomeStepBoundary';
import { wlog, wdims } from '../../utils/welcomeDebug';

import Step0Business from './steps/Step0Business';
import Step1Name from './steps/Step1Name';
import Step2Location from './steps/Step2Location';
import Step3Team from './steps/Step3Team';
import Step4Services from './steps/Step4Services';
import Step5Pricing from './steps/Step5Pricing';
import Step6AssignServices from './steps/Step6AssignServices';
import Step7Group from './steps/Step7Group';
import Step7bGroupSizes from './steps/Step7bGroupSizes';
import Step8Schedule from './steps/Step8Schedule';
import Step9Processing from './steps/Step9Processing';
import Step10Summary from './steps/Step10Summary';

// Mapa stepKey → componente. assign_i / schedule_i são resolvidos dinamicamente.
const STEP_COMPONENTS = {
  theme: Step0Business,
  userName: Step1Name,
  locationName: Step2Location,
  team: Step3Team,
  services: Step4Services,
  servicePricing: Step5Pricing,
  groupQuestion: Step7Group,
  groupSizes: Step7bGroupSizes,
  processing: Step9Processing,
  summary: Step10Summary,
};

const getStepComponent = (stepKey) => {
  if (stepKey?.startsWith('assign_')) return Step6AssignServices;
  if (stepKey?.startsWith('schedule_')) return Step8Schedule;
  return STEP_COMPONENTS[stepKey] || null;
};

export const WelcomeWizard = () => {
  const wizard = useWelcomeWizard();
  // wizard.revealSkip é estável (useCallback no hook) — não recria o timer a cada render.
  const inactivityTimer = useInactivityTimer(60000, wizard.revealSkip);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const containerRef = useRef(null);

  const steps = wizard.state.steps;
  const currentIndex = wizard.state.currentStepIndex;
  const currentKey = wizard.currentStepKey;
  const viewportRef = useRef(null);
  const trackRef = useRef(null);

  wlog('WelcomeWizard', 'RENDER', {
    currentIndex,
    currentKey,
    steps,
    transformY: `-${currentIndex * 100}%`,
    template: wizard.template?.id || 'NENHUM',
  });

  // DEBUG: mede as dimensões reais do DOM depois que o render pinta
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      wlog('WelcomeWizard', '═══ MEDIÇÃO DOM (pós-render) ═══', { currentIndex, currentKey });
      wdims('WelcomeWizard', 'container', containerRef.current);
      wdims('WelcomeWizard', 'viewport', viewportRef.current);
      wdims('WelcomeWizard', 'track', trackRef.current);
      const slides = trackRef.current?.children || [];
      wlog('WelcomeWizard', `track tem ${slides.length} slides`);
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        wdims('WelcomeWizard', `slide[${i}] key=${steps[i]}`, slide);
        wlog('WelcomeWizard', `slide[${i}] conteúdo`, {
          key: steps[i],
          temFilhos: slide.childElementCount,
          htmlVazio: slide.innerHTML.length < 50,
          htmlLen: slide.innerHTML.length,
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  });

  // ===== Trava o scroll do body e esconde o widget de suporte enquanto o wizard vive =====
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const style = document.createElement('style');
    style.id = 'welcome-hide-chrome';
    // Esconde o widget de suporte/chat — nada além das perguntas deve aparecer.
    style.textContent =
      '#support-widget-container,[id^="support-widget"],[class*="support-widget"]{display:none !important;}';
    document.head.appendChild(style);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.getElementById('welcome-hide-chrome')?.remove();
    };
  }, []);

  // ===== Reseta o timer de inatividade a cada interação =====
  const resetTimer = useCallback(() => inactivityTimer.reset(), [inactivityTimer]);
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    c.addEventListener('keydown', resetTimer);
    c.addEventListener('pointerdown', resetTimer);
    c.addEventListener('input', resetTimer);
    return () => {
      c.removeEventListener('keydown', resetTimer);
      c.removeEventListener('pointerdown', resetTimer);
      c.removeEventListener('input', resetTimer);
    };
  }, [resetTimer]);

  // Reseta o timer também a cada troca de tela.
  useEffect(() => {
    inactivityTimer.reset();
  }, [currentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const themeColors = wizard?.template?.colors || {};
  const containerBg = themeColors.background || '#ffffff';
  const textSecondary = themeColors.textSecondary || '#5f6368';

  // A barra de progresso aparece em todas as telas de pergunta (some só na Tela 0).
  const showProgressBar = !!currentKey && currentKey !== 'theme';
  const showSkipLink =
    wizard.showSkip &&
    !!currentKey &&
    currentKey !== 'theme' &&
    currentKey !== 'processing' &&
    currentKey !== 'summary';

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: containerBg,
        overflow: 'hidden',
      }}
    >
      {/* Barra de progresso única e fixa no topo */}
      {showProgressBar && (
        <Box sx={{ flexShrink: 0 }}>
          <WelcomeProgressBar stepIndex={wizard.stepIndex} wizard={wizard} />
        </Box>
      )}

      {/* Viewport do trilho vertical */}
      <Box ref={viewportRef} sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <Box
          ref={trackRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: prefersReducedMotion
              ? 'none'
              : 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: `translateY(-${currentIndex * 100}%)`,
            willChange: 'transform',
          }}
        >
          {steps.map((stepKey, idx) => {
            const StepComponent = getStepComponent(stepKey);
            const professionalIndex =
              stepKey.startsWith('assign_') || stepKey.startsWith('schedule_')
                ? parseInt(stepKey.split('_')[1], 10)
                : null;
            // Só monta a tela quando o usuário chega nela (ou já passou por ela).
            const isMounted = idx <= currentIndex;

            wlog('WelcomeWizard', `map slide[${idx}]`, {
              stepKey,
              isMounted,
              temComponente: !!StepComponent,
              nomeComponente: StepComponent?.name || StepComponent?.displayName || '???',
              professionalIndex,
            });

            return (
              <Box
                key={stepKey}
                component="section"
                sx={{
                  height: '100%',
                  width: '100%',
                  flexShrink: 0,
                  overflowY: 'auto',
                }}
              >
                {isMounted && StepComponent && (
                  <WelcomeStepBoundary stepKey={stepKey}>
                    <StepComponent
                      wizard={wizard}
                      goNext={wizard.goNext}
                      goBack={wizard.goBack}
                      professionalIndex={professionalIndex}
                    />
                  </WelcomeStepBoundary>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Link "Pular" — discreto, só após 60s de inatividade */}
      {showSkipLink && (
        <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 10 }}>
          <Link
            component="button"
            onClick={() => setSkipConfirmOpen(true)}
            sx={{
              fontSize: '12px',
              color: textSecondary,
              textDecorationColor: textSecondary,
            }}
          >
            Pular configuração
          </Link>
        </Box>
      )}

      {/* Confirmação do "Pular" */}
      <Dialog open={skipConfirmOpen} onClose={() => setSkipConfirmOpen(false)}>
        <DialogTitle>Tem certeza que quer pular?</DialogTitle>
        <DialogContent>
          Você pode configurar tudo isso depois, com calma — o que achar melhor.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipConfirmOpen(false)}>Continuar configurando</Button>
          <Button
            variant="contained"
            onClick={() => {
              wizard.skipWizard();
              setSkipConfirmOpen(false);
              window.location.href = '/dashboard';
            }}
          >
            Pular mesmo assim
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WelcomeWizard;
